import { Divider } from "@mui/material";
import { type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { Image } from "@/components/Image/Image";
import { Select, SelectItem } from "@/components/Select";
import { type PaymentType, usePaymentContext } from "@/contexts/PaymentContext";
import { includeBnplFee, calculateMonthlyInstallment } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

type PaymentTypeRadioProps = {
  value: PaymentType;
  checked: boolean;
  onChange: (type: PaymentType) => void;
  disabled?: boolean;
  label: ReactNode;
  description: ReactNode;
  badge?: ReactNode;
};

const PaymentTypeRadio = ({
  value,
  checked,
  onChange,
  disabled,
  label,
  description,
  badge,
}: PaymentTypeRadioProps) => {
  return (
    <label className="relative flex flex-1 cursor-pointer flex-col items-start gap-y-2 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:enabled:border-gray-300 has-checked:border-2 has-checked:border-core-accent-500 has-checked:bg-green-50 has-disabled:cursor-not-allowed has-disabled:opacity-60">
      <input
        type="radio"
        name="paymentType"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="peer hidden"
        disabled={disabled}
      />
      {badge ? (
        <span className="absolute end-4 top-0 -translate-y-1/2 rounded-full bg-surface-accent-softer px-2 py-1 text-xs font-medium text-text-primary">
          {badge}
        </span>
      ) : null}
      <div className="text-sm font-medium text-text-primary">{label}</div>
      <div className="text-xs text-text-tertiary">{description}</div>
    </label>
  );
};

type PaymentChooserProps = {
  price: number;
};

export const PaymentChooser = ({ price }: PaymentChooserProps) => {
  const t = useTranslations();
  const {
    paymentType,
    handlePaymentTypeChange,
    selectedInstallments,
    availableInstallments,
    sessionId,
    setInstallments,
  } = usePaymentContext();

  const isBnplDisabled = !sessionId;

  return (
    <>
      <Image src="/bnpl.png" className="rounded-xl" />

      <div className="my-4 w-full">
        <div className="flex w-full gap-3">
          <PaymentTypeRadio
            value="standard"
            checked={paymentType === "standard"}
            onChange={handlePaymentTypeChange}
            label={t("payment.standardPayment")}
            description={t("payment.payUpfrontDescription")}
          />

          <PaymentTypeRadio
            value="bnpl"
            checked={paymentType === "bnpl"}
            onChange={handlePaymentTypeChange}
            label={t("payment.payLaterPayment")}
            description={t("payment.splitPaymentDescription")}
            badge={isBnplDisabled ? null : t("payment.flexibleBadge")}
            disabled={isBnplDisabled}
          />
        </div>

        <div className="mt-4 rounded-xl bg-surface-neutral-soft p-4">
          {paymentType === "standard" && (
            <>
              <p className="mb-1 text-sm font-medium text-text-title">
                {t("payment.price")}
              </p>
              <div className="text-xl font-bold text-text-primary">
                {formatCurrency(price)}
              </div>
            </>
          )}
          {paymentType === "bnpl" && (
            <>
              <p className="text-start text-sm font-medium text-text-primary">
                {t("payment.calculator")}
              </p>
              <p className="mb-5 text-start text-xs text-text-tertiary">
                {t("payment.calculatorDescription")}
              </p>
              <Select
                label={t("payment.numberOfInstalments")}
                value={selectedInstallments}
                onChange={(e) => setInstallments(Number(e.target.value))}
              >
                {availableInstallments?.map((num) => (
                  <SelectItem key={num} value={num}>
                    {t("payment.months", { count: num })}
                  </SelectItem>
                ))}
              </Select>

              <Divider sx={{ my: 2 }} />

              <div className="mb-1 text-sm font-medium text-text-title">
                {t("payment.monthlyPayment")}
              </div>
              <div className="text-xl font-bold text-text-primary">
                <span>
                  {formatCurrency(
                    calculateMonthlyInstallment(
                      includeBnplFee(price),
                      selectedInstallments,
                    ),
                  )}
                </span>{" "}
                <span className="text-xs font-normal text-text-tertiary">
                  {t("payment.perMonth")}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
